import React, { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { NewModal } from 'Views/Components/Modal/NewModal';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import { LottieSpinner } from 'Views/Components/_HumanWritten/Lottie/LottieSpinner';
import {
	PaymentCheckOutProvider,
	usePaymentCheckOut,
} from 'Views/Components/_HumanWritten/Payment/PaymentCheckoutProvider';
import PaymentTypeWrapper from 'Views/Components/_HumanWritten/Payment/PaymentTypeWrapper';
import { PAYMENT_FORM_ID } from 'Views/Components/_HumanWritten/Payment/usePayment';
import { useSetupPayment, SetupPaymentError } from 'Hooks/useSetupPayment';
import { getUnpaidTransactionForBooking } from 'Services/Api/_HumanWritten/PaymentService';
import { formatPriceDisplay } from 'Util/_HumanWritten/PriceFormattingUtils';
import Skeleton from 'Views/Components/_HumanWritten/Skeleton/Skeleton';
import alertToast from 'Util/ToastifyUtils';

/** Id of the scrollable modal body, so we can reset its scroll when the loader appears. */
const COMPLETE_PAYMENT_BODY_ID = 'complete-payment-modal-body';

interface FlowProps {
	/** The unpaid booking being settled. */
	bookingId: string;
	/** The booking owner — drives the per-type payment gating. */
	ownerUserId: string;
	/** Outstanding amount, for display. */
	unpaidValue: number;
	/** Called after a successful payment so the caller can refresh / continue. */
	onSuccess: () => void;
	/** Called when the user cancels/closes the flow without paying. */
	onClose: () => void;
	/** Settle in check-in context — affects pricing/notes and surfaces EFTPOS as a method. */
	inCheckIn?: boolean;
}

interface Props extends FlowProps {
	isOpen: boolean;
}

/**
 * The "complete payment" flow content for an unpaid booking. Resolves the booking's unpaid
 * transaction, mounts the shared payment UI (limited to the owner's allowed methods — no
 * saved-card option), and renders the modal header/body/footer as a fragment.
 *
 * Rendered as a fragment (not its own NewModal) so it works both declaratively (wrapped in a
 * NewModal — see CompletePaymentModal) and imperatively (inside the global modal chrome — see
 * openCompletePaymentModal). Must be rendered inside a PaymentCheckOutProvider and a NewModal.
 */
function CompletePaymentFlow({
	bookingId,
	ownerUserId,
	unpaidValue,
	onClose,
	onSuccess,
	inCheckIn = false,
}: FlowProps) {
	const initializedRef = useRef(false);

	const {
		data: transactionId,
		isLoading: resolvingTransaction,
		isError: resolveFailed,
		error: resolveError,
	} = useQuery({
		queryKey: ['unpaid-transaction', bookingId],
		queryFn: () => getUnpaidTransactionForBooking(bookingId),
		// A 404 means the booking has no outstanding unpaid transaction — it's already been paid.
		// That's a "nothing to do" success (handled below), not something to retry into an error.
		retry: (failureCount, error) => (error as any)?.response?.status !== 404 && failureCount < 3,
	});

	const {
		transactionDetails: fetchedTransactionDetails,
		error: setupError,
		isLoading: setupLoading,
	} = useSetupPayment({
		transactionId: transactionId ?? null,
		redirectUrl: '',
		isEventBooking: false,
		inCheckIn,
	});

	// The booking turned out to be already settled — reconcile the UI gracefully instead of
	// showing a payment error. Two ways we get here:
	//   • the unpaid transaction no longer exists (404) — it was paid already; or
	//   • setup-payment returned NO_PAYMENT_REQUIRED — the transaction's payment had already
	//     succeeded, so the backend completed the booking during setup (healing a "half-paid"
	//     booking that was charged but never marked paid) and reports that no payment is owed.
	// In both cases the money is (or was) taken, so we toast, refresh the caller, and close.
	const alreadyPaid =
		(resolveFailed && (resolveError as any)?.response?.status === 404) ||
		setupError === SetupPaymentError.NO_PAYMENT_REQUIRED;
	const handledAlreadyPaidRef = useRef(false);
	useEffect(() => {
		if (alreadyPaid && !handledAlreadyPaidRef.current) {
			handledAlreadyPaidRef.current = true;
			alertToast('This booking has already been paid.', 'success', 'Already paid');
			onSuccess();
			onClose();
		}
	}, [alreadyPaid, onSuccess, onClose]);

	const { initializePaymentFlow, setTransactionDetails, paymentProcessing, totalPrice } = usePaymentCheckOut();

	// Prefer the amount the payment flow actually fetched for the transaction (always correct, and
	// the figure that will be charged). Fall back to the caller-supplied value while it loads — some
	// callers (e.g. the check-in overview) don't carry the booking's price, so unpaidValue is 0 there.
	const outstanding = totalPrice && totalPrice > 0 ? totalPrice : unpaidValue;
	// While the fetched price isn't in yet and the caller didn't supply one, show a skeleton in the
	// header instead of a misleading $0.00.
	const priceLoading = (totalPrice === null || totalPrice === undefined) && !(unpaidValue > 0);

	useEffect(() => {
		if (transactionId && !initializedRef.current) {
			initializedRef.current = true;
			initializePaymentFlow({
				transactionId,
				userId: ownerUserId,
				paymentLocation: 'complete-payment',
				includeSubmitButtons: false,
				inCheckIn,
				onConfirm: async () => {
					alertToast('Payment completed successfully', 'success', 'Success');
					onSuccess();
					onClose();
				},
			});
		}
	}, [transactionId, ownerUserId, initializePaymentFlow, onSuccess, onClose, inCheckIn]);

	useEffect(() => {
		if (fetchedTransactionDetails) {
			setTransactionDetails(fetchedTransactionDetails);
		}
	}, [fetchedTransactionDetails, setTransactionDetails]);

	const onPayClick = () => {
		const form = document.getElementById(PAYMENT_FORM_ID) as HTMLFormElement | null;
		form?.requestSubmit();
	};

	const isLoading = resolvingTransaction || setupLoading;
	// An "already paid" outcome is handled by the effect above (toast + close), so keep the
	// spinner showing for it rather than briefly flashing the payment-error fallback.
	const hasError =
		!alreadyPaid &&
		(resolveFailed || (!isLoading && (setupError !== SetupPaymentError.NONE || !fetchedTransactionDetails)));

	// When we swap the (potentially scrolled) payment form for the centred loader — or start
	// processing a payment — scroll the modal body back to the top so the spinner is actually in
	// view instead of an empty white area below the fold.
	const showLoader = isLoading || alreadyPaid || paymentProcessing;
	useEffect(() => {
		if (showLoader) {
			document.getElementById(COMPLETE_PAYMENT_BODY_ID)?.scrollTo({ top: 0 });
		}
	}, [showLoader]);

	return (
		<>
			<NewModal.Header>
				{/* Stack the title and the outstanding-amount subtitle in a single column so the
				    header's space-between layout doesn't push the subtitle to the top-right.
				    Drop the title's bottom padding so the subtitle sits tight under it; the
				    subtitle matches the title's left padding (pl-6 = $space-sm) and adds space below. */}
				<div className="flex flex-col">
					<NewModal.Title className="!pb-0">Complete payment</NewModal.Title>
					<div className="text-sm text-gray-600 pl-6 pb-6 !my-0 flex items-center gap-2">
						{priceLoading ? (
							<>
								<Skeleton width="70px" height="16px" />
								<span>outstanding on this booking.</span>
							</>
						) : (
							<span>{formatPriceDisplay(outstanding, false)} outstanding on this booking.</span>
						)}
					</div>
				</div>
			</NewModal.Header>
			<NewModal.Body id={COMPLETE_PAYMENT_BODY_ID}>
				{(isLoading || alreadyPaid) && (
					<div className="flex flex-col items-center justify-center p-12">
						<LottieSpinner />
					</div>
				)}
				{!isLoading && !alreadyPaid && hasError && (
					<div className="p-8 text-center text-red-500">
						Could not load payment details for this booking. Please try again.
					</div>
				)}
				{/* Reuse the parking purchase modal's payment-body styling (shared selector in
				    purchase-parking.scss) so the payment details match that flow. */}
				{!isLoading && !alreadyPaid && !hasError && (
					<div className="complete-payment-body">
						<PaymentTypeWrapper />
					</div>
				)}
			</NewModal.Body>
			<NewModal.Footer>
				<Button
					display={Display.Outline}
					colors={Colors.Grey}
					newStyles
					onClick={onClose}
					disabled={paymentProcessing}
				>
					Cancel
				</Button>
				<Button
					display={Display.Solid}
					colors={Colors.Primary}
					newStyles
					disabled={paymentProcessing || isLoading || hasError || alreadyPaid}
					onClick={onPayClick}
					buttonProps={{ 'data-test-id': 'complete-payment-pay-now' } as any}
				>
					{paymentProcessing ? 'Processing...' : 'Pay now'}
				</Button>
			</NewModal.Footer>
		</>
	);
}

/**
 * Declarative "complete payment" modal — used where the parent controls open state (booking
 * detail view, check-in booking form).
 */
export default function CompletePaymentModal(props: Props) {
	if (!props.isOpen) return null;
	return (
		<PaymentCheckOutProvider>
			<NewModal
				isOpen
				label="Complete payment"
				onRequestClose={props.onClose}
				modalSize="md"
				className="complete-payment-modal"
				shouldCloseOnOverlayClick={false}
				shouldCloseOnEsc={false}
			>
				<CompletePaymentFlow {...props} />
			</NewModal>
		</PaymentCheckOutProvider>
	);
}

/**
 * Drives a declarative {@link CompletePaymentModal} from the check-in store so the check-in flow
 * can settle an unpaid booking before check-in. The modal is rendered by a host inside the
 * check-in page (see CheckInView) — it must live inside the app's Router/providers because the
 * payment form uses `useNavigate`, so it cannot be launched via the global `store.modal` (which
 * renders outside the Router).
 */
